import React, { useState, useEffect, useCallback } from 'react';
import {
  Wallet, History, RefreshCw, Loader2, Info, AlertTriangle,
  ChevronLeft, ChevronRight, TrendingUp, Shield, CircleDollarSign,
  Search
} from 'lucide-react';
import { api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

// ── Costanti ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

const TYPE_LABELS = {
  sale_credit:      { label: 'Vendita',           color: 'text-green-700',  bg: 'bg-green-50' },
  platform_fee:     { label: 'Commissione',        color: 'text-orange-700', bg: 'bg-orange-50' },
  payout:           { label: 'Payout',             color: 'text-blue-700',   bg: 'bg-blue-50' },
  refund:           { label: 'Rimborso',            color: 'text-red-700',    bg: 'bg-red-50' },
  dispute_hold:     { label: 'Hold contest.',      color: 'text-yellow-800', bg: 'bg-yellow-50' },
  dispute_release:  { label: 'Contest. vinta',     color: 'text-green-700',  bg: 'bg-green-50' },
  dispute_loss:     { label: 'Contest. persa',     color: 'text-red-700',    bg: 'bg-red-50' },
  adjustment:       { label: 'Rettifica',          color: 'text-gray-700',   bg: 'bg-gray-50' },
};

const TYPE_FILTER_OPTIONS = [
  { value: '', label: 'Tutti' },
  { value: 'sale_credit',     label: 'Vendite' },
  { value: 'platform_fee',    label: 'Commissioni' },
  { value: 'payout',          label: 'Payout' },
  { value: 'refund',          label: 'Rimborsi' },
  { value: 'dispute_hold',    label: 'Hold' },
  { value: 'dispute_release', label: 'Contest. vinte' },
  { value: 'dispute_loss',    label: 'Contest. perse' },
];

const STATUS_LABELS = {
  confirmed: { label: 'Conf.',     cls: 'bg-green-100 text-green-700' },
  pending:   { label: 'Attesa',    cls: 'bg-yellow-100 text-yellow-700' },
  cancelled: { label: 'Annull.',   cls: 'bg-gray-100 text-gray-500' },
};

const fmt = (v) => {
  if (v == null) return '—';
  return `${v >= 0 ? '+' : '-'}€${Math.abs(v).toFixed(2)}`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ── Componente ───────────────────────────────────────────────────────────────

export default function AdminVendorFinancePanel({ producerId, producerName }) {
  const [balance, setBalance] = useState(null);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [balanceLoading, setBalanceLoading] = useState(true);

  const loadBalance = useCallback(async () => {
    if (!producerId) return;
    setBalanceLoading(true);
    try {
      const res = await api.get(`/admin/vendors/${producerId}/balance`);
      setBalance(res.data);
    } catch {
      toast.error('Errore nel caricamento del saldo');
    } finally {
      setBalanceLoading(false);
    }
  }, [producerId]);

  const loadEntries = useCallback(async (p = 1, typeF = typeFilter) => {
    if (!producerId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, page_size: PAGE_SIZE });
      if (typeF) params.set('type', typeF);
      const res = await api.get(`/admin/vendors/${producerId}/ledger?${params}`);
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
      setPage(p);
    } catch {
      toast.error('Errore nel caricamento dei movimenti');
    } finally {
      setLoading(false);
    }
  }, [producerId, typeFilter]);

  useEffect(() => {
    if (producerId) {
      loadBalance();
      loadEntries(1, '');
    }
  }, [producerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (newType) => {
    setTypeFilter(newType);
    loadEntries(1, newType);
  };

  const handleRefresh = () => {
    loadBalance();
    loadEntries(page, typeFilter);
  };

  const isNegative = balance && balance.total_balance < 0;

  if (!producerId) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-text-muted">
        <Search className="w-10 h-10 mb-3" />
        <p className="text-sm">Seleziona un produttore per visualizzare il ledger</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold flex items-center gap-2">
            <Wallet className="w-4 h-4 text-primary" />
            Ledger — {producerName || producerId}
          </h3>
          <p className="text-xs text-text-muted mt-0.5">ID: {producerId}</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} className="gap-1.5">
          <RefreshCw className="w-3.5 h-3.5" />
          Aggiorna
        </Button>
      </div>

      {/* Banner saldo negativo */}
      {isNegative && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">
            <strong>Saldo negativo:</strong> il vendor ha un debito. I payout sono bloccati
            finché il saldo totale non torna positivo.
          </p>
        </div>
      )}

      {/* Cards saldo */}
      <div className="grid grid-cols-3 gap-3">
        <MiniCard
          icon={<TrendingUp className="w-3.5 h-3.5" />}
          label="Disponibile"
          value={balance?.available_balance}
          loading={balanceLoading}
          colorClass={balance?.available_balance < 0 ? 'text-red-600' : 'text-green-700'}
        />
        <MiniCard
          icon={<Shield className="w-3.5 h-3.5" />}
          label="Hold dispute"
          value={balance ? Math.abs(balance.hold_balance) : null}
          loading={balanceLoading}
          prefix="−€"
          colorClass="text-yellow-700"
        />
        <MiniCard
          icon={<CircleDollarSign className="w-3.5 h-3.5" />}
          label="Totale"
          value={balance?.total_balance}
          loading={balanceLoading}
          colorClass={isNegative ? 'text-red-600' : 'text-gray-800'}
        />
      </div>

      {/* Tabella movimenti */}
      <div className="bg-white border border-border rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="text-sm font-medium flex items-center gap-1.5">
            <History className="w-3.5 h-3.5" />
            Movimenti
            <span className="text-text-muted font-normal text-xs">({total})</span>
          </span>
          <select
            value={typeFilter}
            onChange={e => handleFilterChange(e.target.value)}
            className="text-xs border border-border rounded px-2 py-1 bg-white focus:outline-none"
          >
            {TYPE_FILTER_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-text-muted" />
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-10 text-text-muted">
            <Info className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm">Nessun movimento trovato</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/30">
                  <tr>
                    <th className="text-left px-3 py-2 text-text-muted font-medium">Data</th>
                    <th className="text-left px-3 py-2 text-text-muted font-medium">Tipo</th>
                    <th className="text-left px-3 py-2 text-text-muted font-medium">Riferimento</th>
                    <th className="text-right px-3 py-2 text-text-muted font-medium">Importo</th>
                    <th className="text-right px-3 py-2 text-text-muted font-medium">Saldo dopo</th>
                    <th className="text-left px-3 py-2 text-text-muted font-medium">Stato</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {entries.map(entry => {
                    const ti = TYPE_LABELS[entry.type] || { label: entry.type, color: 'text-gray-700', bg: 'bg-gray-50' };
                    const si = STATUS_LABELS[entry.status] || { label: entry.status, cls: 'bg-gray-100 text-gray-500' };
                    const isPos = entry.amount >= 0;
                    return (
                      <tr key={entry.id} className={`hover:bg-muted/20 ${entry.type === 'dispute_hold' ? 'bg-yellow-50/50' : ''}`}>
                        <td className="px-3 py-2 text-text-muted whitespace-nowrap">{fmtDate(entry.created_at)}</td>
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 rounded ${ti.bg} ${ti.color} font-medium`}>{ti.label}</span>
                        </td>
                        <td className="px-3 py-2 text-text-secondary max-w-[180px] truncate">
                          {entry.reference || '—'}
                        </td>
                        <td className={`px-3 py-2 text-right font-mono font-semibold ${isPos ? 'text-green-700' : 'text-red-600'}`}>
                          {fmt(entry.amount)}
                        </td>
                        <td className={`px-3 py-2 text-right font-mono ${
                          entry.balance_after == null ? 'text-text-muted' :
                          entry.balance_after < 0 ? 'text-red-500' : 'text-text-secondary'
                        }`}>
                          {entry.balance_after != null ? `€${entry.balance_after.toFixed(2)}` : '—'}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 rounded ${si.cls}`}>{si.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {total > PAGE_SIZE && (
              <div className="flex items-center justify-between px-3 py-2.5 border-t border-border text-xs text-text-muted">
                <span>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} di {total}</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => loadEntries(page - 1)}
                    disabled={page === 1 || loading}
                    className="p-1 border rounded hover:bg-muted/30 disabled:opacity-40"
                  >
                    <ChevronLeft className="w-3 h-3" />
                  </button>
                  <span className="px-2 py-0.5">Pag. {page}</span>
                  <button
                    onClick={() => loadEntries(page + 1)}
                    disabled={page * PAGE_SIZE >= total || loading}
                    className="p-1 border rounded hover:bg-muted/30 disabled:opacity-40"
                  >
                    <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── MiniCard ─────────────────────────────────────────────────────────────────

function MiniCard({ icon, label, value, loading, colorClass, prefix }) {
  const displayValue = () => {
    if (loading || value == null) return '—';
    if (prefix) return `${prefix}${Math.abs(value).toFixed(2)}`;
    return `${value >= 0 ? '' : '-'}€${Math.abs(value).toFixed(2)}`;
  };

  return (
    <div className="bg-muted/20 border border-border rounded-lg p-3">
      <div className="flex items-center gap-1.5 text-xs text-text-muted mb-1">
        {icon}
        {label}
      </div>
      {loading ? (
        <div className="h-6 w-16 bg-muted/50 rounded animate-pulse" />
      ) : (
        <p className={`text-lg font-bold ${colorClass}`}>{displayValue()}</p>
      )}
    </div>
  );
}
